import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { useSaveDealAndNavigate } from 'api/aws/deals/useSaveDealAndNavigate'
import { useEntitiesFromSearch } from 'api/search'
import { initialDealState } from 'components/Deals/grid/initialDealState'
import { useMemo, useState } from 'react'
import type { NewDeal, Product } from 'types'
import {
  buildMasterAgreements,
  calculateNextVersion,
  getHighestVersion,
  normalizeTypeForVersioning,
  type VersionChangeType,
} from 'utils/agreementVersioning'
import VersionAmendmentDialog from './VersionAmendmentDialog'

interface CreateVersionFromDealProps {
  open: boolean
  onClose: () => void
  deal: NewDeal
  productRows: Product[]
}

// Two change-type options post-061526-revision:
//   major  → "Add/Remove Products" (always requires new contract — adds/removes
//            products, possibly new service lines, always triggers a signature)
//   patch  → "Change Order" (in-place pricing OR date adjustments to existing
//            products; no new contract required)
// `minor` was the old "Date Renewal" option — removed per finance. Date edits
// are now available on BOTH remaining types via the sidebar so a Change Order
// can move dates alongside any pricing tweaks.
const changeTypeOptions: { value: VersionChangeType; label: string; description: string }[] = [
  {
    value: 'major',
    label: 'Add/Remove Products',
    description: 'Add or remove products — new contract required',
  },
  {
    value: 'patch',
    label: 'Change Order',
    description: 'Adjust pricing and/or dates on existing products — no new contract',
  },
]

const CreateVersionFromDeal = ({ open, onClose, deal, productRows }: CreateVersionFromDealProps) => {
  const [changeType, setChangeType] = useState<VersionChangeType | ''>('')
  const [showAmendmentDialog, setShowAmendmentDialog] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isError, setIsError] = useState(false)
  const { handleSaveDealAndNavigate } = useSaveDealAndNavigate()
  const { deals: allDeals, products: allProducts } = useEntitiesFromSearch()

  // Build master agreement context from this deal's org
  const { agreement, currentVersion } = useMemo(() => {
    if (!deal?.PK) return { agreement: null, currentVersion: null }
    const orgDeals = allDeals?.filter(d => d?.PK === deal.PK) ?? []
    const orgProducts = allProducts?.filter(p => p?.PK === deal.PK) ?? []
    const { agreements } = buildMasterAgreements(orgDeals, orgProducts)

    // Find the agreement that contains this deal
    const match = agreements.find(a => a.versions.some(v => v.deal.deal_uuid === deal.deal_uuid))
    if (!match) return { agreement: null, currentVersion: null }

    // Find this deal's versioned entry
    const version = match.versions.find(v => v.deal.deal_uuid === deal.deal_uuid) ?? match.currentVersion
    return { agreement: match, currentVersion: version }
  }, [deal, allDeals, allProducts])

  const nextVersion = useMemo(() => {
    if (!currentVersion || !changeType) return ''
    // Use the highest version in the entire chain to avoid collisions
    const baseVersion = agreement ? getHighestVersion(agreement) : currentVersion.semanticVersion
    return calculateNextVersion(baseVersion, changeType)
  }, [currentVersion, agreement, changeType])

  // Build the new deal scaffold from the current deal
  const newDealBase = useMemo((): NewDeal => {
    const normalizedType = normalizeTypeForVersioning(deal.type)
    return {
      ...initialDealState,
      organizations: deal.organizations ?? [],
      contacts: deal.contacts ?? [],
      owner: deal.owner ?? [],
      recipients: deal.recipients ?? {},
      primaryContact: deal.primaryContact ?? [],
      type: normalizedType,
      name: `${deal.name?.split(' - ')[0] || ''} - ${normalizedType}`,
      stage: 'Proposal',
      status: 'Draft',
      startDate: deal.startDate ?? '',
      endDate: deal.endDate ?? '',
      predecessorId: deal.deal_uuid,
    } as NewDeal
  }, [deal])

  const handleNext = (_event: React.MouseEvent<HTMLButtonElement>) => {
    if (!changeType || !agreement || !currentVersion) return
    // All change types go through the amendment dialog
    setShowAmendmentDialog(true)
  }

  const handleClose = () => {
    setChangeType('')
    setShowAmendmentDialog(false)
    onClose()
  }

  if (!agreement || !currentVersion) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Create Version</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary' }}>
            No versionable agreement found for this deal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <>
      <Dialog open={open && !showAmendmentDialog} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Create New Version</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {deal.name}
            </Typography>
            <Chip
              label={`v${currentVersion.semanticVersion}`}
              size="small"
              color="default"
              sx={{ fontFamily: 'monospace', fontWeight: 700, height: 20, fontSize: 10 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Change Type</InputLabel>
            <Select
              label="Change Type"
              value={changeType}
              onChange={e => setChangeType(e.target.value as VersionChangeType)}
            >
              {changeTypeOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1">{opt.label}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      — {opt.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {changeType && nextVersion && (
              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Next version:
                </Typography>
                <Chip
                  label={`v${nextVersion}`}
                  size="small"
                  color="primary"
                  sx={{ fontFamily: 'monospace', fontWeight: 700, height: 20, fontSize: 11 }}
                />
              </Box>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          <Button
            onClick={handleNext}
            variant="contained"
            color="secondary"
            disabled={!changeType || isCreating}
          >
            {isCreating ? 'Creating...' : 'Next'}
          </Button>
        </DialogActions>
      </Dialog>

      {showAmendmentDialog && (changeType === 'major' || changeType === 'minor' || changeType === 'patch') && (
        <VersionAmendmentDialog
          open={showAmendmentDialog}
          onClose={() => setShowAmendmentDialog(false)}
          currentVersion={currentVersion}
          activeAgreement={agreement}
          changeType={changeType}
          newDealBase={newDealBase}
          nextVersion={nextVersion}
          onSaveComplete={handleClose}
        />
      )}
    </>
  )
}

export default CreateVersionFromDeal
